import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { X, AlertTriangle, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useTagAssignment } from "@/hooks/useTagAssignment";

import { MappedTagMinimal } from "@/utils/tagMapping";
type Tag = MappedTagMinimal;

interface TagRemovalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deviceId?: string;
  deviceIds?: string[];
  deviceName?: string;
  onRemovalComplete?: () => void;
  onComplete?: () => void;
}

export const TagRemovalDialog = ({
  open,
  onOpenChange,
  deviceId,
  deviceIds,
  deviceName,
  onRemovalComplete,
  onComplete
}: TagRemovalDialogProps) => {
  const [assignedTags, setAssignedTags] = useState<Tag[]>([]);
  const [bulkTagData, setBulkTagData] = useState<{
    commonTags: Tag[];
    partialTags: Map<string, { tag: Tag; count: number }>;
  }>({ commonTags: [], partialTags: new Map() });
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const { toast } = useToast();
  const { getAssignedTags, removeTag } = useTagAssignment("device");

  useEffect(() => {
    if (open && deviceId) {
      fetchAssignedTags();
    }
  }, [open, deviceId]);

  useEffect(() => {
    if (open && deviceIds && deviceIds.length > 0) {
      fetchBulkTags();
    }
  }, [open, deviceIds]);

  const fetchAssignedTags = async () => {
    if (!deviceId) return;
    
    setIsLoading(true);
    try {
      const tags = await getAssignedTags(deviceId);
      setAssignedTags(tags);
      setSelectedTagIds([]);
    } catch (error) {
      console.error("Error fetching assigned tags:", error);
      toast({
        title: "Error",
        description: "Failed to fetch tags. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBulkTags = async () => {
    if (!deviceIds || deviceIds.length === 0) return;
    
    setIsLoading(true);
    try {
      const allTagsPromises = deviceIds.map(id => getAssignedTags(id));
      const allTagsArrays = await Promise.all(allTagsPromises);
      
      const tagCounts = new Map<string, { tag: Tag; count: number }>();
      
      allTagsArrays.forEach((tags) => {
        tags.forEach((tag) => {
          const existing = tagCounts.get(tag.id);
          if (existing) {
            existing.count++;
          } else {
            tagCounts.set(tag.id, { tag, count: 1 });
          }
        });
      });
      
      const common: Tag[] = [];
      const partial = new Map<string, { tag: Tag; count: number }>();
      
      tagCounts.forEach((value, key) => {
        if (value.count === deviceIds.length) {
          common.push(value.tag);
        } else {
          partial.set(key, value);
        }
      });
      
      setBulkTagData({ commonTags: common, partialTags: partial });
      setSelectedTagIds([]);
    } catch (error) {
      console.error("Error fetching bulk tags:", error);
      toast({
        title: "Error",
        description: "Failed to fetch tags. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTagSelection = (tagId: string, checked: boolean) => {
    if (checked) {
      setSelectedTagIds([...selectedTagIds, tagId]);
    } else {
      setSelectedTagIds(selectedTagIds.filter(id => id !== tagId));
    }
  };

  const handleRemoveTags = () => {
    if (selectedTagIds.length === 0) {
      toast({
        title: "No tags selected",
        description: "Please select at least one tag to remove.",
        variant: "destructive",
      });
      return;
    }
    setShowConfirmation(true);
  };

  const confirmRemoval = async () => {
    if (!deviceId && !deviceIds) return;

    setIsRemoving(true);
    
    try {
      const targetDeviceIds = deviceIds && deviceIds.length > 0 ? deviceIds : [deviceId!];
      let successCount = 0;
      let failCount = 0;

      for (const devId of targetDeviceIds) {
        for (const tagId of selectedTagIds) {
          const success = await removeTag(devId, tagId);
          if (success) {
            successCount++;
          } else {
            failCount++;
          }
        }
      }

      if (failCount === 0) {
        toast({
          title: "Tags removed",
          description: `Successfully removed ${selectedTagIds.length} tag(s) from ${targetDeviceIds.length} device(s).`,
        });
      } else {
        toast({
          title: "Partial success",
          description: `Removed ${successCount} tag assignments. ${failCount} failed.`,
          variant: "destructive",
        });
      }

      setShowConfirmation(false);
      setSelectedTagIds([]);
      
      // Refresh the assigned tags
      if (deviceId) {
        await fetchAssignedTags();
      } else if (deviceIds && deviceIds.length > 0) {
        await fetchBulkTags();
      }
      
      onRemovalComplete?.();
      onComplete?.();
      
    } catch (error) {
      console.error("Error removing tags:", error);
      toast({
        title: "Error",
        description: "Failed to remove tags. Please try again.",
        variant: "destructive",
      });
      setShowConfirmation(false);
    } finally {
      setIsRemoving(false);
    }
  };

  const deviceCount = deviceIds && deviceIds.length > 0 ? deviceIds.length : 1;

  return (
    <>
      <Dialog open={open && !showConfirmation} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Remove Tags from {deviceIds && deviceIds.length > 1 
                ? `${deviceIds.length} devices` 
                : deviceName || "Device"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : deviceId && !deviceIds ? (
              assignedTags.length === 0 ? (
                <Alert>
                  <AlertDescription>
                    No tags are currently assigned to this device.
                  </AlertDescription>
                </Alert>
              ) : (
                <>
                  <div className="text-sm text-muted-foreground">
                    Select tags to remove from this device
                  </div>

                  <ScrollArea className="h-64 border rounded-md p-4">
                    <div className="space-y-2">
                      {assignedTags.map((tag) => (
                        <div
                          key={tag.id}
                          className="flex items-center space-x-3 p-2 rounded-md hover:bg-muted/50 transition-colors"
                        >
                          <Checkbox
                            id={`tag-${tag.id}`}
                            checked={selectedTagIds.includes(tag.id)}
                            onCheckedChange={(checked) =>
                              handleTagSelection(tag.id, checked as boolean)
                            }
                          />
                          <label
                            htmlFor={`tag-${tag.id}`}
                            className="flex-1 flex items-center gap-2 cursor-pointer"
                          >
                            <Badge
                              style={{ backgroundColor: tag.color }}
                              className="text-white"
                            >
                              {tag.name}
                            </Badge>
                            {tag.description && (
                              <span className="text-xs text-muted-foreground">
                                {tag.description}
                              </span>
                            )}
                            <Badge variant="outline" className="ml-auto text-xs">
                              {tag.type}
                            </Badge>
                          </label>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>

                  {selectedTagIds.length > 0 && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium">
                        {selectedTagIds.length} tag{selectedTagIds.length !== 1 ? "s" : ""} selected
                      </span>
                    </div>
                  )}
                </>
              )
            ) : deviceIds && deviceIds.length > 0 ? (
              bulkTagData.commonTags.length === 0 && bulkTagData.partialTags.size === 0 ? (
                <Alert>
                  <AlertDescription>
                    No tags are assigned to any of the selected devices.
                  </AlertDescription>
                </Alert>
              ) : (
                <>
                  <div className="text-sm text-muted-foreground">
                    Select tags to remove from {deviceIds.length} devices
                  </div>

                  <ScrollArea className="h-64 border rounded-md p-4">
                    <div className="space-y-3">
                      {/* Common Tags */}
                      {bulkTagData.commonTags.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-success-muted-foreground mb-2">
                            Common Tags (on all {deviceIds.length} devices)
                          </p>
                          <div className="space-y-2">
                            {bulkTagData.commonTags.map((tag) => (
                              <div
                                key={tag.id}
                                className="flex items-center space-x-3 p-2 rounded-md bg-success-muted hover:bg-success-muted/80 transition-colors"
                              >
                                <Checkbox
                                  id={`tag-${tag.id}`}
                                  checked={selectedTagIds.includes(tag.id)}
                                  onCheckedChange={(checked) =>
                                    handleTagSelection(tag.id, checked as boolean)
                                  }
                                />
                                <label
                                  htmlFor={`tag-${tag.id}`}
                                  className="flex-1 flex items-center gap-2 cursor-pointer"
                                >
                                  <Badge
                                    style={{ backgroundColor: tag.color }}
                                    className="text-white"
                                  >
                                    {tag.name}
                                  </Badge>
                                  {tag.description && (
                                    <span className="text-xs text-muted-foreground">
                                      {tag.description}
                                    </span>
                                  )}
                                  <Badge variant="outline" className="ml-auto text-xs">
                                    {tag.type}
                                  </Badge>
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Partial Tags */}
                      {bulkTagData.partialTags.size > 0 && (
                        <div>
                          <p className="text-xs font-medium text-warning-muted-foreground mb-2">
                            Partial Tags (on some devices)
                          </p>
                          <div className="space-y-2">
                            {Array.from(bulkTagData.partialTags.values()).map(({ tag, count }) => (
                              <div
                                key={tag.id}
                                className="flex items-center space-x-3 p-2 rounded-md bg-warning-muted hover:bg-warning-muted/80 transition-colors"
                              >
                                <Checkbox
                                  id={`tag-${tag.id}`}
                                  checked={selectedTagIds.includes(tag.id)}
                                  onCheckedChange={(checked) =>
                                    handleTagSelection(tag.id, checked as boolean)
                                  }
                                />
                                <label
                                  htmlFor={`tag-${tag.id}`}
                                  className="flex-1 flex items-center gap-2 cursor-pointer"
                                >
                                  <Badge
                                    style={{ backgroundColor: tag.color }}
                                    className="text-white"
                                  >
                                    {tag.name}
                                  </Badge>
                                  <Badge variant="secondary" className="text-xs">
                                    {count}/{deviceIds.length}
                                  </Badge>
                                  {tag.description && (
                                    <span className="text-xs text-muted-foreground">
                                      {tag.description}
                                    </span>
                                  )}
                                  <Badge variant="outline" className="ml-auto text-xs">
                                    {tag.type}
                                  </Badge>
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </ScrollArea>

                  {selectedTagIds.length > 0 && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium">
                        {selectedTagIds.length} tag{selectedTagIds.length !== 1 ? "s" : ""} selected
                      </span>
                    </div>
                  )}
                </>
              )
            ) : null}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRemoveTags}
              disabled={selectedTagIds.length === 0 || isLoading}
            >
              <X className="mr-2 h-4 w-4" />
              Remove Selected Tags
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Confirm Tag Removal
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove {selectedTagIds.length} tag
              {selectedTagIds.length !== 1 ? "s" : ""} from {deviceCount} device
              {deviceCount !== 1 ? "s" : ""}?
              <br />
              <br />
              This action cannot be undone. You can reassign tags later if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRemoving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRemoval}
              disabled={isRemoving}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isRemoving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Removing...
                </>
              ) : (
                <>
                  <X className="mr-2 h-4 w-4" />
                  Remove Tags
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
